<script setup lang="ts">
/**
 * Charpente de l'application : la navigation, le sélecteur d'établissement, et
 * l'avertissement d'inactivité.
 *
 * Les écrans d'affichage et la page de connexion s'en passent — ils déclarent
 * `chrome: false`. Un écran accroché dans un couloir n'a pas de menu.
 */
import { computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useAuthStore } from './stores/auth';
import { useHospitalStore } from './stores/hospital';
import NavigationPrincipale from './components/NavigationPrincipale.vue';

const route = useRoute();
const auth = useAuthStore();
const hospital = useHospitalStore();

const showChrome = computed(() => route.meta['chrome'] !== false && auth.isAuthenticated);

onMounted(async () => {
  if (auth.isAuthenticated) await hospital.loadHospitals();
});

watch(
  () => auth.isAuthenticated,
  async (connected) => {
    if (connected) await hospital.loadHospitals();
  },
);

async function changeHospital(event: Event): Promise<void> {
  const value = (event.target as HTMLSelectElement).value;
  await hospital.select(value);
}
</script>

<template>
  <div v-if="showChrome" class="min-h-screen md:flex">
    <aside
      class="hidden w-60 shrink-0 flex-col justify-between px-6 py-8 md:flex"
    >
      <div>
        <p class="text-title font-light leading-none tracking-tight text-ink">Fitgo</p>
        <p class="mt-1 text-meta text-ink-3">{{ hospital.current?.name ?? '' }}</p>

        <NavigationPrincipale class="mt-10" />
      </div>

      <div>
        <label v-if="hospital.hospitals.length > 1" class="block">
          <span class="text-meta text-ink-3">Établissement</span>
          <select
            class="mt-1 w-full rounded-md bg-tile px-3 py-2 text-body font-light text-ink"
            :value="hospital.currentId ?? ''"
            @change="changeHospital"
          >
            <option v-for="item in hospital.hospitals" :key="item.id" :value="item.id">
              {{ item.name }}
            </option>
          </select>
        </label>

        <p class="mt-6 text-label text-ink-2">{{ auth.user?.displayName }}</p>
        <button
          class="mt-1 text-meta text-ink-3 underline-offset-2 hover:underline"
          type="button"
          @click="auth.logout()"
        >
          Se déconnecter
        </button>
      </div>
    </aside>

    <main class="min-w-0 flex-1 px-5 pb-24 pt-6 md:px-10 md:pb-10">
      <div
        v-if="auth.idleWarning"
        class="mb-5 rounded-md bg-panel px-4 py-3 text-label text-ink-2"
        role="status"
      >
        Personne n’a touché cet écran depuis un moment. La session se fermera
        bientôt ; un geste suffit à la garder ouverte.
      </div>

      <RouterView />
    </main>

    <NavigationPrincipale
      variant="bottom"
      class="fixed inset-x-0 bottom-0 z-10 border-0 bg-bg/95 px-2 py-2 backdrop-blur md:hidden"
    />
  </div>

  <RouterView v-else />
</template>
